<template>
  <!-- Lazy Loader Overlay -->
  <Transition name="loader-fade">
    <div v-if="isLoading" class="loader-overlay">
      <div class="loader-content">
        <div class="loader-logo">
          <span class="logo-dot" v-for="n in 3" :key="n" :style="{ animationDelay: `${(n - 1) * 0.18}s` }"></span>
        </div>
        <div class="loader-bar-track">
          <div class="loader-bar"></div>
        </div>
        <p class="loader-text">Loading, please wait…</p>
      </div>
    </div>
  </Transition>

  <!-- Main App -->
  <template v-if="!isLoading">
    <HeaderComponent />
    <div :class="theme">
      <slot></slot>
    </div>
    <FooterComponent />
  </template>
</template>

<script setup>
import { ref, provide, onMounted } from 'vue';
import HeaderComponent from './components/HeaderComponent.vue';
import FooterComponent from './components/FooterComponent.vue';

const theme = ref('light');
const isLoading = ref(true);

const toggleTheme = () => {
  theme.value = theme.value === 'light' ? 'dark' : 'light';
};

provide('theme', theme);
provide('toggleTheme', toggleTheme);

onMounted(() => {
  // Hide loader once the page fully loads (fonts, images, scripts)
  if (document.readyState === 'complete') {
    isLoading.value = false;
  } else {
    window.addEventListener('load', () => {
      // Small buffer so the loader doesn't flash-disappear
      setTimeout(() => { isLoading.value = false; }, 300);
    });
  }
});
</script>

<style scoped>
/* ── Overlay ─────────────────────────────────────────────── */
.loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Center card ─────────────────────────────────────────── */
.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
}

/* ── Bouncing dots ───────────────────────────────────────── */
.loader-logo {
  display: flex;
  gap: 10px;
}

.logo-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #111;
  animation: dotBounce 0.9s ease-in-out infinite alternate;
}

@keyframes dotBounce {
  0%   { transform: translateY(0);    opacity: 1; }
  100% { transform: translateY(-14px); opacity: 0.3; }
}

/* ── Progress bar ────────────────────────────────────────── */
.loader-bar-track {
  width: 160px;
  height: 3px;
  background: #e5e5e5;
  border-radius: 99px;
  overflow: hidden;
}

.loader-bar {
  height: 100%;
  width: 40%;
  background: #111;
  border-radius: 99px;
  animation: barSlide 1.1s ease-in-out infinite;
}

@keyframes barSlide {
  0%   { transform: translateX(-100%); }
  50%  { transform: translateX(160%);  }
  100% { transform: translateX(-100%); }
}

/* ── Label ───────────────────────────────────────────────── */
.loader-text {
  font-family: system-ui, sans-serif;
  font-size: 13px;
  letter-spacing: 0.08em;
  color: #999;
  margin: 0;
  text-transform: uppercase;
}

/* ── Exit transition ─────────────────────────────────────── */
.loader-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.loader-fade-leave-to {
  opacity: 0;
  transform: scale(1.03);
}
</style>